import { Product, ProductFeature } from "../model/product";
import { useAuth0 } from "@auth0/auth0-react";

export function useProductAPI() {
  const { getAccessTokenSilently } = useAuth0();

  const getProduct = async (id: string, includeStripe: boolean = false): Promise<Product> => {
    const accessToken = await getAccessTokenSilently({
      authorizationParams: {
        audience: process.env.REACT_APP_AUTH0_AUD,
        scope: "read:current_user",
      },
    });

    const params = new URLSearchParams();
    if (includeStripe) {
      params.append("includeStripe", "true");
    }

    return new Promise<Product>((resolve, reject) => {
      fetch(`${process.env.REACT_APP_API_URL}/product/admin/${id}?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }).then((response) => {
        if (response.status === 200) {
          response.json().then((data: Product) => {
            resolve(data);
          });
        } else if (response.status === 404) {
          reject(new Error("Product not found"));
        } else {
          reject(new Error("Failed to fetch product"));
        }
      }).catch((error) => {
        reject(error);
      });
    });
  };

  const createProduct = async (product: Product): Promise<Product> => {
    const accessToken = await getAccessTokenSilently({
      authorizationParams: {
        audience: process.env.REACT_APP_AUTH0_AUD,
        scope: "read:current_user",
      },
    });

    return new Promise<Product>((resolve, reject) => {
      fetch(`${process.env.REACT_APP_API_URL}/product/admin`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(product),
      }).then((response) => {
        if (response.status === 201) {
          response.json().then((data: Product) => {
            resolve(data);
          });
        } else {
          response.json().then((err: any) => {
            reject(new Error(err.error || JSON.stringify(err)));
          }).catch(() => {
            reject(new Error(response.statusText));
          });
        }
      }).catch((error) => {
        reject(error);
      });
    });
  };

  const updateProduct = async (id: string, product: Product): Promise<Product> => {
    const accessToken = await getAccessTokenSilently({
      authorizationParams: {
        audience: process.env.REACT_APP_AUTH0_AUD,
        scope: "read:current_user",
      },
    });

    return new Promise<Product>((resolve, reject) => {
      fetch(`${process.env.REACT_APP_API_URL}/product/admin/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(product),
      }).then((response) => {
        if (response.status === 200) {
          response.json().then((data: Product) => {
            resolve(data);
          });
        } else {
          response.json().then((err: any) => {
            reject(new Error(err.error || JSON.stringify(err)));
          }).catch(() => {
            reject(new Error(response.statusText));
          });
        }
      }).catch((error) => {
        reject(error);
      });
    });
  };

  const deleteProduct = async (id: string): Promise<void> => {
    const accessToken = await getAccessTokenSilently({
      authorizationParams: {
        audience: process.env.REACT_APP_AUTH0_AUD,
        scope: "read:current_user",
      },
    });

    return new Promise<void>((resolve, reject) => {
      fetch(`${process.env.REACT_APP_API_URL}/product/admin/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }).then((response) => {
        if (response.status === 204) {
          resolve();
        } else {
          reject(new Error("Failed to delete product"));
        }
      }).catch((error) => {
        reject(error);
      });
    });
  };

  const getProducts = async (
    includeStripe: boolean = false,
    query: string = "",
    active?: boolean,
    industryType?: string,
    limit: number = 1000,
    offset: number = 0
  ): Promise<Product[]> => {
    const accessToken = await getAccessTokenSilently({
      authorizationParams: {
        audience: process.env.REACT_APP_AUTH0_AUD,
        scope: "read:current_user",
      },
    });

    const params = new URLSearchParams();
    if (includeStripe) {
      params.append("includeStripe", "true");
    }
    if (query) {
      params.append("q", query);
    }
    if (active !== undefined) {
      params.append("active", active.toString());
    }
    if (industryType) {
      params.append("industryType", industryType);
    }
    params.append("limit", limit.toString());
    params.append("offset", offset.toString());

    return new Promise<Product[]>((resolve, reject) => {
      fetch(`${process.env.REACT_APP_API_URL}/product/admin/list?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }).then((response) => {
        if (response.status === 200) {
          response.json().then((data: Product[]) => {
            resolve(data);
          });
        } else {
          reject(new Error("Failed to fetch products"));
        }
      }).catch((error) => {
        reject(error);
      });
    });
  };

  const getProductFeatures = async (productId: string): Promise<ProductFeature[]> => {
    const accessToken = await getAccessTokenSilently({
      authorizationParams: {
        audience: process.env.REACT_APP_AUTH0_AUD,
        scope: "read:current_user",
      },
    });

    return new Promise<ProductFeature[]>((resolve, reject) => {
      fetch(`${process.env.REACT_APP_API_URL}/product/admin/${productId}/features`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      }).then((response) => {
        if (response.status === 200) {
          response.json().then((data: ProductFeature[]) => {
            resolve(data);
          });
        } else {
          reject(new Error("Failed to fetch product features"));
        }
      }).catch((error) => {
        reject(error);
      });
    });
  };

  const setProductFeature = async (productId: string, feature: ProductFeature): Promise<ProductFeature> => {
    const accessToken = await getAccessTokenSilently({
      authorizationParams: {
        audience: process.env.REACT_APP_AUTH0_AUD,
        scope: "read:current_user",
      },
    });

    return new Promise<ProductFeature>((resolve, reject) => {
      fetch(`${process.env.REACT_APP_API_URL}/product/admin/${productId}/features`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(feature),
      }).then((response) => {
        if (response.status === 200) {
          response.json().then((data: ProductFeature) => {
            resolve(data);
          });
        } else {
          response.json().then((err: any) => {
            reject(new Error(err.error || JSON.stringify(err)));
          }).catch(() => {
            reject(new Error(response.statusText));
          });
        }
      }).catch((error) => {
        reject(error);
      });
    });
  };

  return {
    getProduct,
    createProduct,
    updateProduct,
    deleteProduct,
    getProducts,
    getProductFeatures,
    setProductFeature,
  };
}

